import { useEffect, useRef, useState } from 'react';
import { ui } from '../copy/ui';
import { Button } from './Button';
import { cx } from './cx';
import s from './CopyButton.module.css';

type CopyResult = 'idle' | 'copied' | 'failed';

/** Ghost by default. Secondary is for the one copy that is the point of its screen — the
 *  reveal's Copy token — where a ghost reads as a caption beside the value. */
export type CopyButtonVariant = 'ghost' | 'secondary';

/** What this control says about itself once pressed. */
const RESULT: Record<CopyResult, string> = {
  idle: '',
  copied: ui.copied,
  failed: ui.copyFailed,
};

const RESET_AFTER_MS = 2000;

/**
 * Props are closed rather than the usual spread of button attributes: this control owns
 * its click, and an `onClick` arriving from a call site would either be dropped or would
 * replace the copy. `value` is read only inside that handler and never reaches state, a
 * ref, an attribute or a log — one caller passes the one-time API token token.
 */
export interface CopyButtonProps {
  value: string;
  label: string;
  /**
   * Draws the control as a copy glyph, with `label` as its accessible name and its
   * tooltip. For a value that sits beside its own text, such as an id in a definition
   * list, where a worded button would outweigh the value it copies.
   */
  icon?: boolean;
  variant?: CopyButtonVariant;
  /**
   * Takes focus when the Dialog it is in opens. Rendered as `data-autofocus`, the mark
   * Dialog looks for after showModal(), never as React's `autoFocus`, which focuses while
   * the dialog is still display:none and is lost.
   */
  autoFocus?: boolean;
  /**
   * The accessible name in the worded form, when the visible word alone does not say what
   * is copied: a short label on every row of a table, whose name also says which row's
   * value it copies. Start it with the visible word. Without it the button is named by its
   * text; the icon form is always named by `label`.
   */
  name?: string;
}

/** Two overlapping sheets: the copy glyph. Painted by class, never by attribute, so its
 *  colour is the button's text colour and nothing a call site can reach. */
function CopyGlyph() {
  return (
    <svg className={s.glyph} aria-hidden="true" focusable="false" width="16" height="16" viewBox="0 0 16 16">
      <rect x="5.5" y="5.5" width="8" height="8" rx="1.5" />
      <path d="M10.5 3.5v-0.5a1.5 1.5 0 0 0-1.5-1.5h-5a1.5 1.5 0 0 0-1.5 1.5v5a1.5 1.5 0 0 0 1.5 1.5h0.5" />
    </svg>
  );
}

export function CopyButton({
  value,
  label,
  icon = false,
  variant = 'ghost',
  autoFocus = false,
  name,
}: CopyButtonProps) {
  const [result, setResult] = useState<CopyResult>('idle');
  const timer = useRef<number | undefined>(undefined);
  const mounted = useRef(true);

  /**
   * The dialog holding this button can close while the clipboard promise is still pending,
   * so the flag is checked after the await as well as the timer being cleared here: a
   * settle after unmount would otherwise schedule a timeout nothing is left to cancel.
   */
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      if (timer.current !== undefined) window.clearTimeout(timer.current);
    };
  }, []);

  async function copy() {
    if (timer.current !== undefined) window.clearTimeout(timer.current);
    let next: CopyResult;
    try {
      await navigator.clipboard.writeText(value);
      next = 'copied';
    } catch {
      next = 'failed';
    }
    if (!mounted.current) return;
    setResult(next);
    timer.current = window.setTimeout(() => setResult('idle'), RESET_AFTER_MS);
  }

  return (
    <span className={s.wrap}>
      <Button
        variant={variant}
        size="sm"
        data-autofocus={autoFocus ? '' : undefined}
        {...(icon ? { 'aria-label': label, title: label } : name === undefined ? {} : { 'aria-label': name })}
        onClick={() => {
          void copy();
        }}
      >
        {icon ? <CopyGlyph /> : label}
      </Button>
      {result !== 'idle' && (
        <span className={cx(s.result, result === 'failed' && s.failed)} aria-hidden="true">
          {RESULT[result]}
        </span>
      )}
      {/* Mounted empty and never unmounted: a live region that arrives in the same paint as
          its first text is not reliably announced. */}
      <span className={s.srOnly} aria-live="polite">
        {RESULT[result]}
      </span>
    </span>
  );
}

export default CopyButton;
